import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chart } from 'chart.js';
import dayjs from '../../utils/dayjsConfig';
import { SHORT_MONTH_KEYS } from '../../utils/allFarmViewFormatters';

export const MiniGraph = ({
  data,
  timeZone,
  customStyle = {},
  maintainAspectRatio = true,
}) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const [samplesData, setSamplesData] = useState(data);
  const [drawingGraph, setDrawingGraph] = useState(false);
  useEffect(() => {
    setSamplesData(data);
  }, [data]);

  useEffect(() => {
    if (data !== undefined) {
      if (data.length !== samplesData.length || !drawingGraph) {
        initGraph();
      }
    }
  }, [samplesData]);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, []);

  const initGraph = () => {
    if (samplesData === undefined) return;
    setDrawingGraph(true);
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }
    let dataForGraph = Object.values(samplesData).flat(1);
    let samples;
    let lables = [];
    samples = dataForGraph.map((samp, index) => {
      if (samp === undefined) {
        return null;
      } else {
        lables.push(dayjs.unix(samp.date).tz(timeZone));
        return {
          x: dayjs.unix(samp.date).tz(timeZone),
          y: parseFloat(samp.value),
        };
      }
    });
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.canvas.width = customStyle.width || 60;
    ctx.canvas.height = customStyle.height || 30;
    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, 'rgba(69, 99, 199, 0.12)');
    gradient.addColorStop(1, 'rgba(69, 99, 199, 0.02)');
    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: lables,
        datasets: [
          {
            label: '',
            data: samples,
            fill: 'start',
            tension: 0.4,
            backgroundColor: gradient,
            borderColor: '#626e84',
            borderWidth: 1,
            pointRadius: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio,
        hover: { mode: null },
        plugins: {
          tooltip: {
            enabled: false,
          },
          title: {
            display: false,
          },
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            display: false,
            title: {
              display: false,
            },
            grid: {
              drawOnChartArea: false,
            },
            reverse: true,
            beginAtZero: true,
          },
          y: {
            display: false,
            title: {
              display: false,
            },
            grid: {
              drawOnChartArea: false,
            },
            beginAtZero: true,
          },
        },
      },
    });
  };

  return (
    <div id='chart-wrapper'>
      <canvas
        ref={canvasRef}
        id='chart'
        style={{ marginTop: customStyle.marginTop || '10px' }}
      />
    </div>
  );
};

const GRAPH_HEIGHT = 37;

export const MiniGraphContainer = ({
  data,
  timeZone,
  showDates = true,
  graphHeight = GRAPH_HEIGHT,
  graphWidth = '100%',
  customStyle = {},
}) => {
  const { t } = useTranslation();

  const formatDate = (timestamp) => {
    const d = dayjs.unix(timestamp).tz(timeZone);
    return d.date() + ' ' + t(SHORT_MONTH_KEYS[d.month()]);
  };

  const firstDate =
    data && data.length > 0 ? formatDate(data[data.length - 1].date) : null;
  const lastDate = data && data.length > 0 ? formatDate(data[0].date) : null;
  const latestValue =
    data && data.length > 0 && data[0].value != null
      ? Number(data[0].value).toFixed(0)
      : null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        ...customStyle,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ height: graphHeight }}>
          <MiniGraph
            timeZone={timeZone}
            data={data}
            maintainAspectRatio={false}
            customStyle={{
              width: graphWidth,
              marginTop: '0px',
              height: graphHeight,
            }}
          />
        </div>
        {showDates && firstDate && lastDate && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '12px',
              color: 'var(--color-grey-52)',
              marginTop: '2px',
              fontWeight: 400,
            }}
          >
            <span>{firstDate}</span>
            <span>{lastDate}</span>
          </div>
        )}
      </div>
      {latestValue != null && (
        <span className='text-size-14 text-grey45 ml-1'>{latestValue}</span>
      )}
    </div>
  );
};
